import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import { spas } from '../data/charlotteSpas';

const { FiMapPin, FiClock, FiPhone, FiCheck, FiArrowLeft, FiShare2, FiHeart } = FiIcons;

const SpaDetails = () => {
  const { id } = useParams();
  const spa = spas.find(s => s.id === id);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [id]);

  if (!spa) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-stone-50">
        <div className="text-center">
          <h2 className="text-2xl font-playfair mb-4">Business not found</h2>
          <Link to="/" className="text-indigo-700 hover:underline">Return to Directory</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-stone-50 pb-20">
      {/* Top Image Banner */}
      <div className="h-[40vh] md:h-[50vh] relative">
        <img 
          src={spa.imageUrl} 
          alt={spa.name} 
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-stone-900/30"></div>
        <div className="absolute top-6 left-6 z-10">
          <Link to="/" className="bg-white/90 backdrop-blur-sm p-3 rounded-xl inline-flex items-center justify-center hover:bg-white transition-colors shadow-sm">
            <SafeIcon icon={FiArrowLeft} className="w-5 h-5 text-stone-800" />
          </Link>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 -mt-20 relative z-20">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          
          {/* Main Content Column */}
          <div className="lg:col-span-2 space-y-8">
            {/* Header Card */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-white rounded-3xl p-6 md:p-10 shadow-sm border border-stone-100"
            >
              <div className="inline-block bg-indigo-50 text-indigo-800 px-4 py-1 rounded-full text-xs font-bold mb-6 border border-indigo-100 uppercase tracking-widest">
                {spa.type}
              </div>
              <h1 className="font-playfair text-4xl md:text-5xl font-bold text-stone-900 mb-6">
                {spa.name}
              </h1>
              
              <div className="flex flex-wrap items-center gap-4 text-stone-600 mb-8 pb-8 border-b border-stone-100">
                <div className="flex items-center">
                  <SafeIcon icon={FiMapPin} className="w-5 h-5 mr-2 text-indigo-400" />
                  <span className="font-medium">{spa.neighborhood}</span>
                </div>
                <span className="hidden md:inline text-stone-300">•</span>
                <div className="flex items-center">
                  <SafeIcon icon={FiClock} className="w-5 h-5 mr-2 text-indigo-400" />
                  <span>Open Today: {spa.hours.split('|')[0]}</span>
                </div>
              </div>

              <p className="text-stone-600 text-lg leading-relaxed">
                {spa.description}
              </p>
            </motion.div>

            {/* Services Section */}
            <motion.div 
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="bg-white rounded-3xl p-6 md:p-10 shadow-sm border border-stone-100"
            >
              <h2 className="font-playfair text-2xl font-bold text-stone-900 mb-8">Service Menu & Pricing</h2>
              <div className="divide-y divide-stone-100">
                {spa.services.map((service, index) => (
                  <div key={index} className="py-6 flex justify-between items-center group">
                    <div>
                      <h4 className="text-stone-800 font-bold text-lg">{service.name}</h4>
                      <p className="text-stone-500 text-sm mt-1 flex items-center">
                        <SafeIcon icon={FiClock} className="w-4 h-4 mr-1" /> {service.duration}
                      </p>
                    </div>
                    <div className="text-right">
                      <span className="text-2xl font-bold text-indigo-900">${service.price}</span>
                    </div>
                  </div>
                ))}
              </div>
            </motion.div>
          </div>

          {/* Sidebar Column */}
          <div className="lg:col-span-1">
            <motion.div 
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              className="bg-white rounded-3xl p-8 shadow-sm border border-stone-100 sticky top-24"
            >
              <button className="w-full bg-indigo-900 hover:bg-indigo-950 text-white py-5 rounded-2xl font-bold text-lg transition-all mb-8 shadow-lg hover:shadow-indigo-900/20">
                Contact Business
              </button>

              <div className="space-y-8">
                <div>
                  <h3 className="text-xs font-bold text-stone-400 uppercase tracking-widest mb-4">Location</h3>
                  <div className="flex items-start text-stone-700 font-medium">
                    <SafeIcon icon={FiMapPin} className="w-5 h-5 mr-3 mt-0.5 text-indigo-900 flex-shrink-0" />
                    <span>{spa.address}</span>
                  </div>
                </div>

                <div>
                  <h3 className="text-xs font-bold text-stone-400 uppercase tracking-widest mb-4">Contact Information</h3>
                  <div className="flex items-center text-stone-700 font-medium">
                    <SafeIcon icon={FiPhone} className="w-5 h-5 mr-3 text-indigo-900 flex-shrink-0" />
                    <a href={`tel:${spa.phone.replace(/\D/g,'')}`} className="hover:text-indigo-700">{spa.phone}</a>
                  </div>
                </div>

                <div>
                  <h3 className="text-xs font-bold text-stone-400 uppercase tracking-widest mb-4">Amenities</h3>
                  <div className="grid grid-cols-1 gap-3">
                    {spa.features.map((feature, index) => (
                      <div key={index} className="flex items-center text-stone-700 text-sm">
                        <SafeIcon icon={FiCheck} className="w-4 h-4 text-green-500 mr-2" />
                        {feature}
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            </motion.div>
          </div>

        </div>
      </main>
    </div>
  );
};

export default SpaDetails;